// نبض التعلّم — global positive ticker shown to every user on every page
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PULSE_KIND_AR } from "@/v2/lib/rbac";
import {
  Trophy, Award, Star, Flame, Gem, Rocket, Building2, Sparkles, Zap, Megaphone,
  Activity, X, PartyPopper, HandHeart,
} from "lucide-react";

const ICONS = { Trophy, Award, Star, Flame, Gem, Rocket, Building2, Sparkles, Zap, Megaphone };
export const pulseIcon = (name) => ICONS[name] || Sparkles;

const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `قبل ${mins || 1} دقيقة`;
  if (mins < 1440) return `قبل ${Math.round(mins / 60)} ساعة`;
  return `قبل ${Math.round(mins / 1440)} يوم`;
};

export const PulseDetail = ({ item, canCheer, onClose, onCheered }) => {
  const [sending, setSending] = useState(false);
  const I = pulseIcon(item.icon);
  const cheer = async (message) => {
    setSending(true);
    try {
      await api.post(`/pulse/${item.id}/cheer`, { message });
      toast.success("تم إرسال التهنئة 👏");
      onCheered?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الإرسال"); } finally { setSending(false); }
  };
  return (
    <div className="v2-overlay flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="v2-glass v2-glass-gold w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}
        data-testid="pulse-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center"><I size={22} /></div>
            <div>
              <div className="font-extrabold">{item.kind_label || PULSE_KIND_AR[item.kind] || "إنجاز"}</div>
              <div className="text-[11px] text-[#bcc8cf]">{timeAgo(item.at)}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="pulse-modal-close"
            className="w-9 h-9 rounded-full v2-inset flex items-center justify-center"><X size={16} /></button>
        </div>

        <p className="text-lg leading-relaxed mb-4">{item.text}</p>

        <div className="v2-inset p-4 space-y-2 text-sm">
          {item.actor_name && (
            <div className="flex items-center gap-3">
              {item.actor_avatar && <img src={item.actor_avatar} alt="" className="w-9 h-9 rounded-full object-cover" />}
              <div><div className="font-bold">{item.actor_name}</div>
                {item.department && <div className="text-[11px] text-[#bcc8cf]">{item.department}</div>}</div>
            </div>
          )}
          {item.meta?.badge && <div className="text-[#bcc8cf]">الشارة: <span className="text-white">{item.meta.badge}</span></div>}
          {item.meta?.level && <div className="text-[#bcc8cf]">المستوى: <span className="text-white">{item.meta.level}</span></div>}
          {item.meta?.points && <div className="text-[#bcc8cf]">النقاط: <span className="text-white">{item.meta.points}</span></div>}
          {item.meta?.streak && <div className="text-[#bcc8cf]">سلسلة التعلّم: <span className="text-white">{item.meta.streak} أيام</span></div>}
          {item.meta?.challenge && <div className="text-[#bcc8cf]">التحدي: <span className="text-white">{item.meta.challenge}</span></div>}
          {item.cheers > 0 && <div className="text-[#e3c799]">👏 {item.cheers} تهنئة</div>}
        </div>

        <p className="text-sm text-[#91cab8] mt-4">إنجاز يستحق التقدير — استمروا في التقدم! 🎉</p>

        {canCheer && !item.actor_id && (
          <p className="text-xs text-[#bcc8cf] mt-4 v2-inset p-3" data-testid="pulse-no-cheer">
            هذه رسالة عامة للمنصة — لا تحتاج إلى تهنئة موجّهة لموظف.
          </p>
        )}
        {canCheer && item.actor_id && (
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button disabled={sending} onClick={() => cheer("👏 أحسنت على إنجازك، واصل التقدم!")}
              data-testid="pulse-cheer" className="v2-btn v2-btn-sm">👏 هنّئ</button>
            <button disabled={sending} onClick={() => cheer("🌟 إنجاز رائع، نحن فخورون بك!")}
              data-testid="pulse-encourage" className="v2-btn-ghost v2-btn-sm">🌟 شجّع</button>
            <button disabled={sending} onClick={() => cheer("🏆 نبارك لك هذا الإنجاز المميز!")}
              data-testid="pulse-celebrate" className="v2-btn-ghost v2-btn-sm">🏆 احتفل</button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export const usePulse = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["pulse"],
    queryFn: async () => (await api.get("/pulse")).data,
    refetchInterval: (q) => ((q?.state?.data?.refresh_seconds || 1200) * 1000),
    refetchOnWindowFocus: false,
    staleTime: 20 * 60 * 1000,
    enabled: !!user,
  });
};

export const pulseVisible = (d) => !!(d?.enabled && d?.items?.length);

/** تكرار العناصر بما يكفي لملء الشريط ثم مضاعفتها لحلقة لا نهائية بدون فجوات */
const buildTrack = (items) => {
  const reps = Math.max(1, Math.ceil(14 / items.length));
  const base = Array.from({ length: reps }, () => items).flat();
  return { base, loop: [...base, ...base] };
};

export default function PulseBar() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [showCelebration, setShowCelebration] = useState(true);
  const { data } = usePulse();

  useEffect(() => {
    if (!data?.celebration) return;
    setShowCelebration(true);
    const t = setTimeout(() => setShowCelebration(false), 12000);
    return () => clearTimeout(t);
  }, [data?.celebration?.id]);

  if (!pulseVisible(data)) return null;
  const items = data.items;
  const { base, loop } = buildTrack(items);
  const duration = (data.speed || 55) * (base.length / items.length);
  const celebration = data.celebration && showCelebration ? data.celebration : null;

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-40 v2-glass rounded-none border-x-0 border-t-0" data-testid="pulse-bar">
        <div className="flex items-stretch h-11">
          <div className="shrink-0 flex items-center gap-2 px-3 sm:px-4 border-l border-white/10">
            <span className="v2-pulse-dot" />
            <Activity size={15} className="text-[#91cab8]" />
            <span className="text-xs sm:text-sm font-extrabold v2-gold-text whitespace-nowrap">نبض التعلّم</span>
          </div>

          {celebration ? (
            <motion.button onClick={() => setSelected(celebration)} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              data-testid="pulse-celebration"
              className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-3 text-center min-w-0"
              style={{ background: "linear-gradient(90deg, rgba(187,150,97,0.16), rgba(145,202,184,0.16))" }}>
              <PartyPopper size={18} className="text-[#e3c799] v2-float" />
              <span className="font-extrabold text-xs sm:text-sm truncate">{celebration.text}</span>
              <PartyPopper size={18} className="text-[#e3c799] v2-float" />
            </motion.button>
          ) : (
            <div className="v2-pulse-wrap flex-1 flex items-center min-w-0">
              <div className="v2-pulse-track" style={{ animationDuration: `${duration}s` }}>
                {loop.map((it, i) => {
                  const I = pulseIcon(it.icon);
                  return (
                    <button key={`${it.id}-${i}`} className="v2-pulse-item" onClick={() => setSelected(it)}
                      data-testid={i < items.length ? `pulse-item-${i}` : undefined} dir="rtl">
                      {it.actor_avatar
                        ? <img src={it.actor_avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                        : <span className="w-6 h-6 rounded-full bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center shrink-0"><I size={13} /></span>}
                      <span className="text-[#f6f8fa]/90">{it.text}</span>
                      {it.cheers > 0 && <span className="text-[11px] text-[#e3c799]">👏 {it.cheers}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {data.can_cheer && (
            <div className="shrink-0 hidden sm:flex items-center px-3 border-r border-white/10 text-[#bcc8cf]" title="يمكنك تهنئة الموظفين">
              <HandHeart size={16} />
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <PulseDetail item={selected} canCheer={data.can_cheer} onClose={() => setSelected(null)}
            onCheered={() => qc.invalidateQueries({ queryKey: ["pulse"] })} />
        )}
      </AnimatePresence>
    </>
  );
}
